import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ExternalLink, FileText, Building2, Shield } from "lucide-react";

const Footer = () => {
  const resourceLinks = [
    {
      title: "Minnesota Department of Health (MDH)",
      description: "Health regulations and licensing information",
      url: "https://www.health.state.mn.us/",
      icon: Shield,
    },
    {
      title: "Department of Human Services (DHS)",
      description: "Human services and support programs",
      url: "https://mn.gov/dhs/",
      icon: FileText,
    },
    {
      title: "MN Assisted Living Scorecard",
      description: "Quality ratings and facility comparisons",
      url: "https://www.health.state.mn.us/facilities/regulation/infectioncontrol/ltc/assistedliving.html",
      icon: Building2,
    },
  ];

  const platformLinks = [
    { title: "Privacy Policy", href: "/privacy" },
    { title: "Terms of Service", href: "/terms" },
    { title: "Support", href: "/support" },
    { title: "Contact Us", href: "/contact" },
  ];

  return (
    <footer className="bg-gradient-to-r from-primary to-primary-glow text-white">
      <div className="container mx-auto px-4 py-12">
        {/* Resource Links Section */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold mb-6 text-center">
            Resources
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {resourceLinks.map((resource, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <resource.icon className="w-8 h-8 text-white/80 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-2">
                        {resource.title}
                      </h4>
                      <p className="text-white/80 text-sm mb-4">
                        {resource.description}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:border-white/50"
                      >
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center"
                        >
                          Visit Site
                          <ExternalLink className="w-3 h-3 ml-2" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Platform Information */}
        <div className="border-t border-white/20 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* About CareKonnect */}
            <div>
              <h4 className="text-lg font-semibold mb-4">About CareKonnect</h4>
              <p className="text-white/80 text-sm leading-relaxed">
                CareKonnect streamlines the care placement process by connecting 
                Minnesota's assisted living and long-term care providers with 
                county case managers, hospital discharge planners, and social workers.
              </p>
            </div>

            {/* Platform Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Platform</h4>
              <ul className="space-y-2">
                {platformLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-white/80 hover:text-white text-sm transition-colors"
                    >
                      {link.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Information */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <div className="text-white/80 text-sm space-y-2">
                <p>Email: hellocarekonnect@gmail.com</p>
                <p>
                  Hours: Monday - Friday
                  <br />
                  8:00 AM - 6:00 PM CST
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 mt-8 pt-6 text-center">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-white/80 text-sm">
              © {new Date().getFullYear()} CareKonnect. A Minnesota state initiative.
            </div>
            <div className="flex items-center space-x-4 text-white/80 text-sm">
              <span>Serving Minnesota since 2024</span>
              <div className="w-1 h-1 bg-white/40 rounded-full"></div>
              <span>Secure & Compliant</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;